import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output
} from '@angular/core';
import {Host, Menu, MenuItem, AppService, VariantJSON, CartEntry} from "../../../../common";
import {Cart, CartService} from "../../../../common";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {FormBuilder, FormGroup} from "@angular/forms";
import {IChoiceEntry, IItemChoice} from "../../../../common/models/menu";

@Component({
  selector: 'menu-item',
  standalone: false,
  template: `
    <div #card class="menu-item-card card mt-3 mb-3">
      <img class="card-img-top" [src]="item.image" alt="{{ item.name}}" *ngIf="item.image">
      <div class="card-body">
        <div class="card-title h5 row">
          <div class="col">{{ item.name }}
            <div *ngIf="item.quantity && !item.hasVariants()" class="text-muted"><small>{{ item.quantity }}</small>
            </div>
          </div>
          <div class="col-auto text-right" *ngIf="!item.hasVariants()">{{ getPriceWithVariant(null)|currency:host }}
          </div>
        </div>
        <div *ngIf="text" [innerHtml]="text" class="mt-3"></div>
        <div *ngIf="item.hasFootnotes()" class="footnotes mt-3">
          <span i18n>contains </span><span class="footnote-list">
            <a *ngFor="let footnote of item.getFootnotes()">{{ footnote.title }}</a>
          </span>
        </div>
        <constraint-info [method]="cart.method" [constraint]="item.group.constraint"
                         *ngIf="item.group.constraint && !item.constraint"></constraint-info>
        <constraint-info [method]="cart.method" [constraint]="item.constraint"
                         *ngIf="item.constraint"></constraint-info>
        <div class="form">
          <div *ngIf="item.hasVariants()" class="mt-3">
            <h6 i18n>Variants</h6>
            <div class="form-check" *ngFor="let variant of item.variants">
              <input [(ngModel)]="selectedVariant" class="form-check-input hide-validation-status" type="radio"
                     name="variantSelection{{ item.id }}" id="v_{{ variant.id }}" [value]="variant.id">
              <label class="form-check-label row" for="v_{{ variant.id }}">
                <div class="col">{{ variant.name }} <span *ngIf="variant.quantity">({{ variant.quantity }})</span>
                </div>
                <div class="col-auto text-right">{{ getPriceWithVariant(variant)|currency:host }}</div>
              </label>
            </div>
          </div>
          <div *ngIf="choices" class="mt-3" [formGroup]="form">
            <div class="form-group" *ngFor="let choice of choices; let i = index">
              <button *ngIf="i==0" class="btn btn-xs btn-default float-right mb-1 p-0" (click)="randomizeChoices()"><i
                class="fa fa-random"></i></button>
              <label [for]="'id_choice'+choice.id">{{ choice.name }}</label>
              <select class="form-control hide-validation-success" [formControlName]="'choice'+choice.id"
                      [id]="'id_choice'+choice.id" required>
                <option [ngValue]="null" disabled i18n>(select item)</option>
                <option [ngValue]="opt.key" *ngFor="let opt of choice.choices">{{ opt.title }} <span
                  *ngIf="opt.price > 0">(+{{ opt.price|currency:host }})</span></option>
              </select>
            </div>
          </div>
          <option-list [item]="item" [host]="host" [selections]="selectedOptions"></option-list>
        </div>
        <div class="mt-3 mb-0 btn-toolbar justify-content-between" style="align-content: flex-end;" *ngIf="ordersEnabled">
          <div></div>
          <add-to-cart [itemId]="'m'+item.id" (clickAnimationDone)="addToCart()"></add-to-cart>
        </div>
      </div>
    </div>
  `
})
export class MenuItemComponent implements OnInit, OnChanges {

  @Input() menu: Menu = null;
  @Input() cart: Cart = null;
  @Input() item: MenuItem;
  @Output() entryAdded: EventEmitter<CartEntry> = new EventEmitter();

  ordersEnabled: boolean;
  host: Host = null;
  text: SafeHtml = null;
  selectedVariant: number = null;
  selectedOptions: Map<number, boolean>;
  form: FormGroup;
  choices: Array<IItemChoice>;

  @ViewChild('card') cardRef: ElementRef;

  constructor(
    private appService: AppService,
    private cartService: CartService,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.host = this.menu.host;
    this.ordersEnabled = this.appService.getOrdersEnabled();
    this.computeValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['item']) {
      this.computeValues();
    }
  }

  computeValues(): void {
    this.text = (this.item) ? this.sanitizer.bypassSecurityTrustHtml(this.item.text) : '';
    if (this.item.hasVariants()) {
      this.selectedVariant = this.item.getDefaultVariantId();
    }
    if (this.item.hasOptions()) {
      this.selectedOptions = new Map<number, boolean>();
      this.item.option_ids.forEach(id => this.selectedOptions[id] = false);
    }
    this.choices = this.item.getChoices();
    let controlsConfig = {};
    this.choices.forEach((c) => {
      controlsConfig['choice' + c.id] = this.fb.control(null);
    });
    this.form = this.fb.group(controlsConfig);
  }

  randomizeChoices() {
    const choices = {};
    this.choices.forEach((c) => {
      const choice = c.choices[Math.floor(Math.random() * c.choices.length)];
      choices['choice' + c.id] = choice.key;
    });
    this.form.setValue(choices);
  }

  addToCart() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const options = [];
    this.item.option_ids.forEach(id => {
      if (this.selectedOptions[id]) {
        options.push(id);
      }
    });
    this.appService.trackEvent('action', 'addToCart');
    const choices = {};
    for (const [id, choice] of Object.entries(this.getSelectedChoices())) {
      if (choice) {
        choices[id] = choice.key;
      } else {
        choices[id] = null;
      }
    }
    const entry = this.cartService.addToCart({
      item: this.item,
      count: 1,
      variant_id: (this.item.hasVariants()) ? this.selectedVariant : null,
      option_ids: options,
      choices: choices
    });
    this.entryAdded.emit(entry);
  }

  getSelectedChoices(): { [id: number]: IChoiceEntry } {
    const selection = {};
    this.choices.forEach((c) => {
      const value = this.form.value['choice' + c.id];
      selection[c.id] = c.choices.find(x => x.key == value);
    });
    return selection;
  }

  getPriceWithVariant(variant: VariantJSON): number {
    let price = parseFloat((variant) ? variant.price : this.item.price);
    for (const [id, choice] of Object.entries(this.getSelectedChoices())) {
      price += (choice) ? parseFloat(choice.price) : 0;
    }
    if (this.selectedOptions) {
      for (const [optid, selected] of Object.entries(this.selectedOptions)) {
        if (selected) {
          price += parseFloat(this.menu.options[optid].price);
        }
      }
    }
    return price;
  }
}
